import {Card, type ImageAspectRatio} from '@primer/react-brand/esm'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS} from '@contentful/rich-text-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import type {PrimerComponentCard} from '../../../schemas/contentful/contentTypes/primerComponentCard'
import {getPrimerIcon} from '../../../lib/utils/icons'
import {getAnalyticsEvent, documentToPlainTextString} from '../../../lib/utils/analytics'
import {getImageSources} from '../../../lib/utils/images'
import {t} from '../../../lib/utils/localization'
import {codeMarkRenderer, noCodeMarkRenderer, renderSingleLineRichText} from '../shared/RichTextFieldRenderer'

export type ContentfulCardProps = {
  component: PrimerComponentCard

  /**
   * The props below do not have a corresponding field in Contentful:
   */
  hasBorder?: boolean
  className?: string
  fullWidth?: boolean
  imageAspectRatio?: ImageAspectRatio
}

export const ContentfulCard = ({
  component,
  hasBorder = false,
  fullWidth = false,
  imageAspectRatio,
  className,
}: ContentfulCardProps) => {
  const enableCodeBlocks = isFeatureEnabled('contentful_primer_code_blocks')
  const Octicon = getPrimerIcon(component.fields.icon)
  const image = component.fields.image
  const iconColor = component.fields.iconColor || 'default'
  const hasBackground = component.fields.iconBackground
  const variant = component.fields.variant
  const label = component.fields.label
  const labelVariant = component.fields.labelVariant || 'token'
  const ctaText = component.fields.ctaText || t('Learn more')
  const plainTextHeading =
    component.fields.heading ??
    (component.fields.headingRich ? documentToPlainTextString(component.fields.headingRich) : '')

  return (
    <Card
      data-ref={`card-action-${component.sys.id}`}
      align={component.fields.align}
      href={component.fields.href}
      ctaText={ctaText}
      className={className}
      hasBorder={hasBorder}
      fullWidth={fullWidth}
      variant={variant}
      {...getAnalyticsEvent({
        action: 'learn more',
        tag: 'card',
        context: plainTextHeading,
      })}
    >
      {Octicon && (
        <Card.Icon
          data-testid={`${component.fields.icon}-${component.fields.href}`}
          // eslint-disable-next-line react-hooks/static-components
          icon={<Octicon />}
          color={iconColor}
          hasBackground={hasBackground}
        />
      )}

      <Card.Heading as={component.fields.headingLevel}>
        {component.fields.headingRich
          ? renderSingleLineRichText(component.fields.headingRich, enableCodeBlocks)
          : component.fields.heading}
      </Card.Heading>

      {image && (
        <Card.Image
          as="picture"
          src={`${image.fields.file.url}?fm=webp`}
          sources={getImageSources(image.fields.file.url)}
          alt={image.fields.description || ''}
          aspectRatio={imageAspectRatio}
        />
      )}

      {label &&
        (typeof label === 'string' ? (
          <Card.Label>{label}</Card.Label>
        ) : (
          <Card.Label variant={labelVariant}>{label.fields.text}</Card.Label>
        ))}

      {component.fields.description && (
        <Card.Description>
          {documentToReactComponents(component.fields.description, {
            renderMark: enableCodeBlocks ? codeMarkRenderer : noCodeMarkRenderer,
            renderNode: {
              [BLOCKS.PARAGRAPH]: (_, children) => [children, <br key="line-break" />],
            },
          })}
        </Card.Description>
      )}
    </Card>
  )
}
